import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import suburbsData from "../../../data/us_addresses.json";
import UserCard from "./WorkerResultsListComponent";
import daysOfWeekOptions from "./data/daysOfWeek.json";
import filterCareTypeOptions from "./data/filterCareTypes.json";
import genderOptions from "./data/genderOptions.json";
import { RotatingLines } from "react-loader-spinner";
import WorkerProfileResultsComponent from "./WorkerProfileResultsComponent";
import config from "../../../config/config";
import axios from "axios";
import {
  FaCog, FaFilter, FaTimesCircle
} from "react-icons/fa";
import { SlEqualizer } from "react-icons/sl";
import { useUser } from "../../../context/UserContext";
import { useSearchWorkers } from "../../../context/SearchWorkersContext";
import { getStepFromPath } from "../../../util_functions/helpers";

const SearchWorkersResults = () => {
  const { user: clientUser, allWorkerUsers, fetchAllWorkerUsers } = useUser();
  const {
    getSelectionIdFromCookies,
    searchSelections,
    updateSearchSelectionField,
  } = useSearchWorkers();
  const {
    suburbValue,
    childDetails,
    supportTypeDetails,
    careActivityDetails,
    genderDetails,
  } = searchSelections;
  const [suggestions, setSuggestions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 5;
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const baseURL = config.API_BASE_URL;
  const navigate = useNavigate();

  const isMobile = useMediaQuery({ maxWidth: 1024 });
  const [showLeftPanel, setShowLeftPanel] = useState(!isMobile);

  useEffect(() => {
    setShowLeftPanel(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const selectionId = getSelectionIdFromCookies();
    console.log(
      "[SearchWorkersResults] Retrieved selectionId from cookies:",
      selectionId
    );
    if (!selectionId) {
      console.warn("No selectionId found. Redirecting to /search-workers...");
      navigate("/account/fs-dashboard/search-workers");
    }
  }, [getSelectionIdFromCookies, navigate]);

  useEffect(() => {
    if (searchSelections.suburbValue) {
      console.log(
        "[SearchWorkersResults] suburbValue is ready:",
        searchSelections.suburbValue
      );
      setIsReady(true);
    }
  }, [searchSelections]);

  useEffect(() => {
    console.log("[SearchWorkersResults] searchSelections:", searchSelections);
  }, [searchSelections]);

  const filterUsers = (usersData) => {
    const filtered = usersData.filter((user) => {
      const userGender = (user.gender || "No preference").toLowerCase();
      const genderFilter = (genderDetails || "No preference").toLowerCase();

      const matchesGender =
        genderFilter === "no preference" || userGender === genderFilter;

      const matchesSuburb =
        suburbValue &&
        user.serviceLocations &&
        user.serviceLocations.some(
          (location) =>
            location &&
            location.toLowerCase().includes(suburbValue.toLowerCase())
        );

      return matchesGender && matchesSuburb;
    });

    setFilteredUsers(filtered);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchAllWorkerUsers();
      } catch (error) {
        console.error("Error fetching workers from context:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchAllWorkerUsers]);

  useEffect(() => {
    filterUsers(allWorkerUsers);
  }, [
    allWorkerUsers,
    suburbValue,
    childDetails,
    supportTypeDetails,
    careActivityDetails,
    genderDetails,
    suburbValue,
  ]);

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const handleUserCardClick = (user) => {
    const currentStep = getStepFromPath();
    setSelectedUser(user);
    setShowUserProfile(true);
    navigate(`/account/fs-dashboard/search-workers/step-${currentStep + 1}`);
  };

  const handleCloseUserProfile = () => {
    const currentStep = getStepFromPath();
    setShowUserProfile(false);
    navigate(`/account/fs-dashboard/search-workers/step-${currentStep - 1}`);
  };

  const closeFilters = () => {
    setShowFilters(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSuburbChange = (e) => {
    const inputValue = e.target.value;

    updateSearchSelectionField("suburbValue", inputValue);
    const MAX_RESULTS = 5;
    const filteredSuburbs = suburbsData.Suburbs_Zipcodes.filter((suburb) =>
      suburb.Post_City.toLowerCase().startsWith(inputValue.toLowerCase())
    ).slice(0, MAX_RESULTS);

    setSuggestions(filteredSuburbs);
  };

  const handleSuggestionClick = (suburb) => {
    const display = `${suburb.Post_City}, ${suburb.State} ${suburb.Zip_Code}`;
    updateSearchSelectionField("suburbValue", display);
    setSuggestions([]);
  };

  const handleClearInput = () => {
    updateSearchSelectionField("suburbValue", "");
    setSuggestions([]);
  };

  return (
    <div className="search-workers-results-overall-container">
      {showUserProfile ? (
        <div className="worker-profile-independent-container">
          <WorkerProfileResultsComponent
            workerUserId={selectedUser.id}
            onClose={handleCloseUserProfile}
          />
        </div>
      ) : (
        <div className="header-results-container">
          <div className="filter-header-results">
            <h3 className="filter-results-heading">
              <FaFilter className="results-filter-icon" />
              Results Filter
            </h3>
            <h3 className="overall-container-h3">
              <span className="match-count">{filteredUsers.length}</span> potential
              {filteredUsers.length === 1 ? " match" : " matches"} found.
            </h3>
          </div>
          {isMobile && (
            <div className="filter-toggle-container">
              {showLeftPanel ? (
                <div className="filter-hide-results" onClick={() => setShowLeftPanel(false)}>
                  <FaTimesCircle /><span>Hide Filters</span>
                </div>
              ) : (
                <div className="filter-show" onClick={() => setShowLeftPanel(true)}>
                  <SlEqualizer className="filter-toggle-icon" />
                  <span>Show Filters</span>
                </div>
              )}
            </div>
          )}


          <div className="search-workers-results-container">
            {(!isMobile || showLeftPanel) && (
              <div className="search-results-left-overall-container">
                <div className="search-workers-results-left-container">
                  <div className="search-workers-results-left-div">
                    <h4>Suburb</h4>
                    <div className="input-container">
                      <input
                        type="text"
                        id="suburb"
                        className="suburb-input"
                        required
                        value={suburbValue}
                        onChange={handleSuburbChange}
                      />
                      {suburbValue && (
                        <span
                          onClick={handleClearInput}
                          className="clear-icon-with-button"
                        >
                          X
                        </span>
                      )}
                    </div>
                    <div className="location-suggestions">
                      <ul>
                        {suggestions.map((s, i) => (
                          <li key={i} onClick={() => handleSuggestionClick(s)}>
                            {s.Post_City}, {s.State} {s.Zip_Code}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="search-workers-results-left-div">
                    <h4>Availability</h4>
                    {daysOfWeekOptions.days.map((day) => (
                      <label key={day}>
                        <input
                          type="checkbox"
                          className="results-rounded-checkbox"
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                  <div className="search-workers-results-left-div">
                    <h4>Support for children</h4>
                    <label>
                      <input
                        type="checkbox"
                        className="results-rounded-checkbox"
                      />
                      Children check verified
                    </label>
                  </div>
                  <div className="search-workers-results-left-div">
                    <h4>Gender</h4>
                    <select
                      className="gender-filter-select"
                      value={genderDetails || "No preference"}
                      onChange={(e) =>
                        updateSearchSelectionField(
                          "genderDetails",
                          e.target.value
                        )
                      }
                    >
                      {genderOptions.genders.map((gender) => (
                        <option key={gender.id} value={gender.value}>
                          {gender.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="search-workers-results-left-div">
                    <h4>Type of support</h4>
                    {filterCareTypeOptions.careTypes.map((careType, index) => (
                      <label key={index}>
                        <input
                          type="checkbox"
                          className="results-rounded-checkbox"
                        />
                        {careType}
                      </label>
                    ))}
                  </div>
                  <div
                    className="search-workers-results-left-div"
                    id="filters-container"
                    onClick={toggleFilters}
                  >
                    <h4>Show all filters</h4>
                    <div className="workers-filter-settings">
                      <FaCog className="settings-icon-style" />
                      <span className="icon-text1">Settings</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {isMobile && showLeftPanel && (
              <div className="mobile-show-results-button-container">
                <button
                  className="mobile-show-results-button"
                  onClick={() => setShowLeftPanel(false)}
                >
                  Show Results
                </button>
              </div>
            )}
            <div className="search-workers-results-right-container">
              {loading ? (
               <div style={{ marginTop: "20px" }}>
                  <RotatingLines
                   visible={true}
                    height="76"
                   width="76"
                    color="blue"
                    strokeWidth="5"
                    animationDuration="0.75"
                  ariaLabel="rotating-lines-loading"
                    wrapperStyle={{ margin: "auto" }}
                   wrapperClass=""
                 />
               </div>
              ) : filteredUsers.length === 0 ? (
               <div className="no-workers-placeholder">
                  <img
                    src="/images/no_results_found.png"
                    alt="No results found"
                    className="no-workers-image"
                  />
                 <p>No matches found!</p>
                </div>
              ) : (
                <>
                  {currentUsers.map((workerUser) => (
                    <UserCard
                      key={workerUser.id}
                      user={workerUser}
                      clientUser={clientUser}
                      onViewProfile={() => handleUserCardClick(workerUser)}
                    />
                  ))}
                  <div className="active-jobs-pagination">
                    <button
                      onClick={() => handlePageChange("first")}
                      disabled={currentPage === 1}
                    >
                      &laquo;
                    </button>
                    <button
                      onClick={() => handlePageChange("previous")}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </button>
                    <span>
                      Page <strong>{currentPage}</strong> of{" "}
                      <strong>{totalPages > 99 ? "99+" : totalPages}</strong>
                    </span>
                    <button
                      onClick={() => handlePageChange("next")}
                      disabled={currentPage >= totalPages}
                    >
                      &gt;
                    </button>
                    <button
                      onClick={() => handlePageChange("last")}
                      disabled={currentPage >= totalPages}
                    >
                      &raquo;
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchWorkersResults;
